import {
  Alert,
  Card,
  MultiSelect,
  MultiSelectOption,
  SimpleSelect,
  SwitchInput,
  TextInput,
} from 'corso-admin-ui';
import { Pegasus } from 'corso-types';
import { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { match } from 'ts-pattern';
import { useAutomationFormContext } from '~/features/settings/automations/hooks/useAutomationForm';
import { useFactValues } from '~/features/settings/hooks/useFactValues';
import useIsCorsoAdmin from '~/hooks/useIsCorsoAdmin';
import { orderEditActionLabel } from '~/utils/enumNameMaps';
import { requiredConditionsByEditType } from './EventOrderEditingRequiredFacts';

const modificationTypeOptions = [
  { label: 'Add only', value: 'ADD' },
  { label: 'Remove only', value: 'REMOVE' },
  { label: 'Add or remove', value: 'BOTH' },
] as const;

function useShippingLineTitleOptions(): MultiSelectOption[] {
  const factValue = useFactValues();

  const titles = factValue.data?.arbitrary['order/shippingLine/title'] ?? [];

  return titles.map((title) => ({
    value: title,
    label: title,
    keywords: [title],
  }));
}

function EventOrderModifyQuantityFormFields() {
  const { control, register } = useAutomationFormContext();

  return (
    <>
      <Controller
        control={control}
        name="event.params.quantityModificationType"
        defaultValue="BOTH"
        render={({ field: f }) => (
          <SimpleSelect
            options={[...modificationTypeOptions]}
            label="Allowed adjustment type"
            value={f.value}
            onChange={f.onChange}
            required
          />
        )}
      />
      <TextInput
        id="quantity-modification-value"
        label="Per-line cap"
        details="Maximum number of units a customer may add or remove on any single line item."
        {...register('event.params.quantityModificationValue', {
          valueAsNumber: true,
        })}
      />
    </>
  );
}

function EventOrderUpdateShippingLineFormFields() {
  const { control } = useAutomationFormContext();
  const shippingLineTitleOptions = useShippingLineTitleOptions();

  return (
    <Controller
      control={control}
      name="event.params.allowedShippingLines"
      defaultValue={[]}
      render={({ field, fieldState }) => (
        <MultiSelect
          label="Shipping Methods"
          details="The shipping methods a customer can select from when requesting a shipping method update."
          options={shippingLineTitleOptions}
          placeholder="Find or add shipping methods"
          value={field.value.map((title) => ({ label: title, value: title }))}
          onChange={(selected) =>
            field.onChange(selected.map(({ value }) => value))
          }
          error={fieldState.error?.message}
          creatable
        />
      )}
    />
  );
}

function EventOrderCancelFormFields() {
  const { control } = useAutomationFormContext();

  return (
    <>
      <Controller
        control={control}
        name="event.params.shouldNotifyOnCancellation"
        render={({ field: f }) => (
          <SwitchInput
            id="should-notify-on-cancellation"
            label="Notify on Cancellation"
            details="When enabled, the customer will be notified when their order is cancelled."
            checked={f.value}
            onChange={f.onChange}
          />
        )}
      />
      <Controller
        control={control}
        name="event.params.shouldRestockOnCancellation"
        render={({ field: f }) => (
          <SwitchInput
            id="should-restock-on-cancellation"
            label="Restock on Cancellation"
            details="When enabled, the items will be restocked to the inventory when the order is cancelled."
            checked={f.value}
            onChange={f.onChange}
          />
        )}
      />
    </>
  );
}

function EventUpdateShippingAddressFormFields() {
  const { control, watch } = useAutomationFormContext();
  const watchRecalc = watch('event.params.shouldRecalculateTaxesOnAddressEdit');
  const isCorsoAdmin = useIsCorsoAdmin();

  if (!isCorsoAdmin) return null;

  return (
    <>
      <Controller
        control={control}
        name="event.params.allowDifferentCountryAddressEdit"
        render={({ field: f }) => (
          <SwitchInput
            id="allow-different-country-address-edit"
            label="Allow Country Changes"
            details="When enabled, customers can update their shipping address to a different country."
            checked={f.value}
            onChange={f.onChange}
          />
        )}
      />
      <Controller
        control={control}
        name="event.params.shouldRecalculateTaxesOnAddressEdit"
        render={({ field: f }) => (
          <SwitchInput
            id="should-recalculate-taxes-on-address-edit"
            label="Recalculate Taxes on Address Edit"
            details="When enabled, taxes will be recalculated when the shipping address is updated."
            checked={f.value}
            onChange={f.onChange}
          />
        )}
      />
      {watchRecalc && (
        <Alert
          variant="warning"
          heading="If a different tax jurisdiction is detected between the old and new address, the order will be cancelled and recreated to ensure correct tax application."
        />
      )}
    </>
  );
}

// TODO: work out a good way to set recommended conditions on mount!
export default function EventOrderEditing() {
  const { register, watch, setValue } = useAutomationFormContext();

  const editType = watch('event.params.editType');

  const currentConditions = watch('conditions');

  useEffect(() => {
    const requiredConditions = requiredConditionsByEditType[editType];

    const hasAllRequiredConditions = requiredConditions.every((rq) =>
      currentConditions.find((c) =>
        c.fact === rq.fact &&
        c.operator === rq.operator &&
        Array.isArray(rq.value)
          ? rq.value.every((v) => Array.isArray(c.value) && c.value.includes(v))
          : c.value === rq.value,
      ),
    );

    if (hasAllRequiredConditions) return;

    setValue('conditions', requiredConditions);
  }, [currentConditions, editType, setValue]);

  return (
    <div className="flex flex-col gap-4">
      <Card.Heading>{orderEditActionLabel[editType]}</Card.Heading>

      {editType === 'REMOVE_PACKAGE_PROTECTION' && (
        <Alert
          variant="info"
          heading="Window Managed by Corso. The default window and any overrides will not apply to this rule."
        />
      )}

      <TextInput
        id="order-edit-action-confirm-text"
        label="Confirm Message"
        details="Optional message shown to customers before they confirm the action."
        {...register(`event.params.editConfirmLabel`)}
      />
      <TextInput
        id="order-edit-action-success"
        label="Success Message"
        details="The message shown to customers after the action is successfully submitted."
        {...register(`event.params.editSuccessLabel`)}
      />

      {match(editType)
        .with(Pegasus.OrderEditType.CANCEL_ORDER, () => (
          <EventOrderCancelFormFields />
        ))
        .with(Pegasus.OrderEditType.MODIFY_QUANTITY, () => (
          <EventOrderModifyQuantityFormFields />
        ))
        .with(Pegasus.OrderEditType.UPDATE_SHIPPING_LINE, () => (
          <EventOrderUpdateShippingLineFormFields />
        ))
        .with(Pegasus.OrderEditType.UPDATE_SHIPPING_ADDRESS, () => (
          <EventUpdateShippingAddressFormFields />
        ))
        .otherwise(() => null)}
    </div>
  );
}
